import { staticConfig } from "@fidget/common/config/static-config";
import { isValid, parse } from "date-fns";
import { range, reverse } from "lodash";
import React, { RefObject, useRef, useState } from "react";
import { formatNumberWithLeadingZero } from "../../../utils/timeFormatter";
import { AutoComplete } from "../autoComplete/autoComplete";
import styles from "./tripleDatePicker.css";

interface Props {
  onDatePicked(date: Choice): void;
  initiallyOpen?: boolean;
  yearsAhead?: number;
  yearsBackward?: number;
  datum?: Date;
  initialChoice?: Partial<Choice>;
  additionalValidation?(date: Choice): boolean;
  yearsAscending?: boolean;
}
export interface Choice {
  day: number;
  month: number;
  year: number;
}
enum Status {
  PickingDay,
  PickingMonth,
  PickingYear,
  Idle
}
const ANY_LEAP_YEAR = 2020;

export const TripleDatePicker = ({
  onDatePicked,
  yearsAhead = staticConfig.aheadYearsAutoCompleteNumber,
  yearsBackward = staticConfig.backwardYearsAutoCompleteNumber,
  initiallyOpen = true,
  datum = new Date(),
  initialChoice = {},
  additionalValidation = () => true,
  yearsAscending
}: Props) => {
  const ref = useRef<HTMLDivElement>();

  const [status, setStatus] = useState(initiallyOpen ? Status.PickingDay : Status.Idle);
  const [choice, setChoice] = useState<Partial<Choice>>(initialChoice);
  const [hasError, setHasError] = useState(false);

  const maybeHandleOnDatePicked = (newState: Partial<Choice>) => {
    if (newState.day && newState.month && newState.year) {
      onDatePicked(newState as Choice);
    }

    return newState;
  };

  const handleDayPicked = (val: number | null | "") => {
    if (val) {
      setChoice(maybeHandleOnDatePicked({ ...choice, day: val }));
      setStatus(Status.PickingMonth);
      setHasError(false);
    }
  };
  const handleMonthPicked = (val: number | null | "") => {
    if (val) {
      // year is not yet picked, substitute with any leap year for day-month validation
      if (
        choice.day &&
        isValid(parse(`${ANY_LEAP_YEAR}-${val}-${choice.day}`, "yyyy-M-d", new Date()))
      ) {
        setChoice(maybeHandleOnDatePicked({ ...choice, month: val }));
        setStatus(Status.PickingYear);
        setHasError(false);
      } else {
        setChoice({ ...choice, month: val });
        setHasError(true);
        setStatus(Status.PickingDay);
      }
    }
  };
  const handleYearPicked = (val: number | null | "") => {
    if (val) {
      const { day, month } = choice;
      if (
        day &&
        month &&
        isValid(parse(`${val}-${month}-${day}`, "yyyy-M-d", new Date())) &&
        additionalValidation({ day, month, year: val })
      ) {
        setChoice(maybeHandleOnDatePicked({ ...choice, year: val }));
        setStatus(Status.Idle);
      } else {
        setChoice({ ...choice, year: val });
        setStatus(Status.PickingDay);
        setHasError(true);
      }
    }
  };

  const renderDayPicker = () => (
    <AutoComplete<number>
      className={styles["single-input"]}
      onInputClick={() => setStatus(Status.PickingDay)}
      itemsProvider={() => range(1, 32)}
      inputValue={choice.day ? formatNumberWithLeadingZero(choice.day) : "DD"}
      itemToString={(val) => (val ? formatNumberWithLeadingZero(val) : "")}
      onChange={handleDayPicked}
      onStateChange={(state) => (state.isOpen ? setStatus(Status.PickingDay) : undefined)}
      defaultIsOpen={status === Status.PickingDay}
      hasError={hasError}
      readOnly
    />
  );
  const renderMonthPicker = () => (
    <AutoComplete<number>
      className={styles["single-input"]}
      onInputClick={() => setStatus(Status.PickingMonth)}
      itemsProvider={() => range(1, 13)}
      inputValue={choice.month ? formatNumberWithLeadingZero(choice.month) : "MM"}
      itemToString={(val) => (val ? formatNumberWithLeadingZero(val) : "")}
      onChange={handleMonthPicked}
      onStateChange={(state) => (state.isOpen ? setStatus(Status.PickingMonth) : undefined)}
      defaultIsOpen={status === Status.PickingMonth}
      hasError={hasError}
      readOnly
    />
  );

  const yearsRange = range(datum.getFullYear() - yearsBackward, datum.getFullYear() + yearsAhead);
  const renderYearPicker = () => (
    <AutoComplete<number>
      className={styles["single-input"]}
      onInputClick={() => setStatus(Status.PickingYear)}
      itemsProvider={() => (yearsAscending ? yearsRange : reverse(yearsRange))}
      onStateChange={(state) => (state.isOpen ? setStatus(Status.PickingYear) : undefined)}
      inputValue={choice.year?.toString() || "YYYY"}
      itemToString={(item) => (item || "").toString()}
      onChange={handleYearPicked}
      defaultIsOpen={status === Status.PickingYear}
      hasError={hasError}
      readOnly
    />
  );

  return (
    <div ref={ref as RefObject<HTMLDivElement>} className={styles.container}>
      {renderDayPicker()}
      {renderMonthPicker()}
      {renderYearPicker()}
    </div>
  );
};
